import { useDispatch, useSelector } from "react-redux";
import DashboardContentBox from "../DashboardContentBox";
import styles from "../../../styles/Components/new-dashboard/latest-signals/SignalsList.module.css";
import DashboardBadge from "../DashboardBadge";
import { FaRegClock } from "react-icons/fa6";
import { IoNotifications, IoNotificationsOff } from "react-icons/io5";
import React, { useMemo, useState } from "react";
import {
  createWatchList,
  deleteWatchlist,
  modifyWatchlist,
} from "../../../redux/new-insight/actions";
import useServiceStatus from "../../../hooks/useServiceStatus";
import ServiceActivationModal from "../../../shared/service-activation/ServiceActivationModal";
import useFetchWatchlist from "../../../hooks/api/useFetchWatchlist";
import { Button, Popover } from "antd";
import { useRelativeTime } from "../../../hooks/useRelativeTime";
import moment from "moment";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { MdMoreHoriz } from "react-icons/md";
import SignalsTable from "./SignalsTable";

const SignalItem = ({ signal = {}, view }) => {
  const {
    newInsightState: {
      watchlists,
      watchlistStatus,
      createWatchlistStatus,
      modifyWatchListStatus,
      deleteWatchListStatus,
    },
    main: { userData },
  } = useSelector((state) => state);

  const { tp1_percent, tp3_percent, tp5_percent, sl_percent, leverage } =
    signal;

  const positionSize = useMemo(() => {
    const sl = parseFloat(sl_percent);
    const lev = parseFloat(leverage);
    if (!sl || !lev) return null;
    return Math.min(((1 / sl) * 100) / lev, 100).toFixed(2);
  }, [sl_percent, leverage]);

  const popoverContent = (
    <div className={styles.popoverContent}>
      <div className={styles.popoverTitle}>
        <strong>Signal Info</strong>
      </div>
      <div>
        <strong>TP1:</strong> {parseFloat(tp1_percent).toFixed(2)}%
      </div>
      <div>
        <strong>TP3:</strong> {parseFloat(tp3_percent).toFixed(2)}%
      </div>
      <div>
        <strong>TP5:</strong> {parseFloat(tp5_percent).toFixed(2)}%
      </div>
      <div>
        <strong>SL:</strong> {parseFloat(sl_percent).toFixed(2)}%
      </div>
      {positionSize && (
        <div>
          <strong>Pos. Size:</strong> {positionSize}%
        </div>
      )}
    </div>
  );

  const [showServiceActivationModal, setShowServiceActivationModal] =
    useState(false);

  // set for watchlist loading
  const [targetCoin, setTargetCoin] = useState(null);

  const dispatch = useDispatch();

  const { isActive } = useServiceStatus("insight");

  const { symbol, trade_type: type, signal_date: date } = signal;

  const isCoinInList = watchlists?.[0]?.list_of_coins?.includes(symbol);
  const signalDate = useRelativeTime(date);

  const now = moment();
  const signalTime = moment.utc(date).local();
  const diffInMinutes = now.diff(signalTime, "minutes");
  const isRecentSignal = diffInMinutes < 15;

  const handleWatchlistActions = () => {
    if (watchlistStatus === "loading") return;

    // Prevent user to create watchlist if insight service is not active
    if (!isActive) return setShowServiceActivationModal(true);

    setTargetCoin(symbol);

    if (watchlists.length === 0) {
      console.log("create");
      dispatch(
        createWatchList(
          userData["custom:custom_username"],
          userData?.email,
          [symbol],
          true,
        ),
      ).finally(() => setTargetCoin(null));
      return;
    }

    const currentWatchlist = watchlists?.[0];
    const createdDate = currentWatchlist?.createdDate;
    const coins =
      typeof currentWatchlist?.list_of_coins === "string"
        ? JSON.parse(currentWatchlist?.list_of_coins?.replace(/'/g, '"'))
        : currentWatchlist?.list_of_coins;

    if (coins.length === 1 && isCoinInList) {
      console.log("delete");
      dispatch(
        deleteWatchlist(
          userData["custom:custom_username"],
          userData?.email,
          createdDate,
          true,
        ),
      ).finally(() => setTargetCoin(null));
      return;
    }

    const updatedCoinsList = isCoinInList
      ? coins.filter((c) => c !== symbol)
      : [...coins, symbol];
    dispatch(
      modifyWatchlist(
        userData["custom:custom_username"],
        userData?.email,
        createdDate,
        updatedCoinsList,
        true,
      ),
    ).finally(() => setTargetCoin(null));
  };

  useFetchWatchlist();

  return (
    <>
      <DashboardContentBox
        className={`${styles.signalBox} ${styles?.[view]} ${isRecentSignal ? styles.recentSignal : ""}`}
      >
        <div className={styles.header}>
          <div className={styles.leftHeader}>
            <div className={styles.logo}>
              <LazyLoadImage
                src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/symbol_icons/${symbol?.replace("/USDT", "")}.png`}
                alt={symbol}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/placeholders/asset-logo.svg";
                }}
                width="30px"
                height="30px"
                effect="blur"
                style={{ borderRadius: "100%" }}
              />
            </div>
            <div className={styles.info}>
              <h6>
                {symbol}
                {symbol?.includes("/USDT") ? null : "/USDT"}
              </h6>
              <div className={styles.tradeType}>
                <DashboardBadge
                  type={
                    type?.toLowerCase() === "buy"
                      ? "primary"
                      : type === "Sell"
                        ? "dangerous"
                        : "primary"
                  }
                  usage="card"
                  className={styles.badge}
                >
                  {type}
                </DashboardBadge>

                <DashboardBadge
                  type="success"
                  usage="card"
                  className={styles.badge}
                >
                  {type?.toLowerCase() === "buy" ? "Long" : "Short"}
                </DashboardBadge>
              </div>
            </div>
          </div>
          <div className={styles.rightHeader}>
            <FaRegClock />
            {signalDate}
          </div>
        </div>

        <div className={styles.footer}>
          <Popover content={popoverContent} rootClassName={styles.popover}>
            <Button
              type="default"
              size="small"
              className={`${styles.btn} ${styles.popoverButton}`}
            >
              Signal Info
              <MdMoreHoriz size={22} />
            </Button>
          </Popover>

          <Button
            usage="card"
            onClick={handleWatchlistActions}
            style={{ cursor: "pointer", width: "fit-content" }}
            className={`${styles.btn} ${styles.watchListIcon}`}
            loading={
              targetCoin === symbol &&
              (createWatchlistStatus === "loading" ||
                modifyWatchListStatus === "loading" ||
                watchlistStatus === "loading" ||
                deleteWatchListStatus === "loading")
            }
          >
            {targetCoin === symbol &&
            (createWatchlistStatus === "loading" ||
              modifyWatchListStatus === "loading" ||
              watchlistStatus === "loading" ||
              deleteWatchListStatus === "loading") ? (
              ""
            ) : isCoinInList ? (
              <>
                <span className={styles.btnTitle}>Remove from Watchlist</span>
                <IoNotificationsOff size={14} />
              </> // Show IoNotificationsOff if in the list
            ) : (
              <>
                <span className={styles.btnTitle}>Add to Watchlist</span>
                <IoNotifications size={14} />
              </> // Show IoNotifications if not in the list
            )}
          </Button>
        </div>

        {/*<small>*/}
        {/*  <FaRegClock />*/}
        {/*  /!*{view === "list" && "-"}*!/*/}
        {/*  {signalDate}*/}
        {/*</small>*/}
      </DashboardContentBox>

      {showServiceActivationModal && (
        <ServiceActivationModal
          service={"insai"}
          show={showServiceActivationModal}
          setShow={setShowServiceActivationModal}
        />
      )}
    </>
  );
};

function SignalsList({ view = "card" }) {
  const {
    dashboard: {
      signalInfo: { signals = [] },
    },
  } = useSelector((state) => state);

  const sortedSignals = [...signals]?.sort(
    (a, b) => new Date(b?.signal_date) - new Date(a?.signal_date),
  );

  // Render table view for list display
  if (view === "list") {
    return <SignalsTable />;
  }

  // Render card view for card display
  return (
    <div className={`${styles.signals} ${styles?.[view]}`}>
      {sortedSignals.map((signal) => (
        <SignalItem signal={signal} key={signal?.signal_date} view={view} />
      ))}
    </div>
  );
}

export default SignalsList;
